/**
 * Ein einzelner Zähler — `/anlagen/{id}/zaehler/{meterId}` (HM-057, geordnet
 * mit HM-066, in zwei Reiter geteilt mit HM-068 nach dem überarbeiteten
 * `ui_kits/dashboard/MetersScreen.jsx`).
 *
 * **Zwei Ansichten statt einer langen Seite.** Die Übersicht beantwortet die
 * Frage, mit der man einen Zähler öffnet — was steht drauf, wie lief es
 * zuletzt. Alles, was man **einmal** einrichtet und danach jahrelang nicht mehr
 * anfasst — Quellbindungen, Stammdaten, Archivieren —, liegt unter
 * „Einstellungen". Vorher stand beides untereinander, und die Folge sah man der
 * Seite an: Wer nach dem Zählerstand kam, hatte darunter ein Bindungsformular,
 * elf Stammdatenzeilen und einen roten Kasten stehen, die er nicht gesucht
 * hatte. HM-066 hat die Reihenfolge der Fragen berichtigt; die Teilung nimmt
 * die zweite Hälfte aus dem Weg.
 *
 * Damit ist auch der Einwand aus HM-066 hinfällig geworden — „je Reiter eine
 * halbe Karte" galt, solange die Einrichtung aus einer Zeile je Zählwerk
 * bestand. Sie ist seither ein Abschnitt mit Bindungsformular, Stammdaten und
 * rotem Kasten; der Entwurf zieht dieselbe Grenze.
 *
 * **Übersicht:** was steht drauf — Kacheln, Verlauf und die eine Zeile, die
 * sagt, woher der Wert kommt.
 * **Einstellungen:** die Zählwerke mit ihren Quellbindungen, die Stammdaten,
 * der rote Kasten.
 *
 * **Übernommen ist der Aufbau, nicht jede Zusage** — dieselbe Regel wie beim
 * Dashboard und bei der Anmeldung. Der Entwurf lässt den Namen des Zählers auf
 * der Seite weg, weil ihn dort die Kopfleiste trägt; hier trägt sie den Namen
 * des **Bereichs** („Zähler"), wie an der Gerätedetailseite auch. Der Name
 * bleibt deshalb in der Kopfzeile der Seite — die andere Lösung wäre, die
 * Kopfleiste für zwei Detailseiten umzustellen, und das ist eine Änderung am
 * Rahmen und nicht an dieser Seite. Geblieben ist der Gewinn des Entwurfs: eine
 * **Zeile** statt einer Karte, denn Bauart und Nummer stehen ohnehin in den
 * Stammdaten.
 *
 * **Der Verlauf kommt aus der Rohschicht des gebundenen Datenpunkts, nicht aus
 * den Zählwerksreihen.** Die gibt es erst mit HM-008. Das ist keine Notlösung,
 * sondern eine andere Aussage, und sie steht als Satz an der Sektion: Gezeigt
 * wird, was **dieses Gerät an dieser Stelle gemeldet hat** — nicht, was die
 * Messstelle über ihre Zählerwechsel hinweg gezählt hat. Beide Wege bleiben
 * nebeneinander bestehen und werden nicht ineinander übersetzt (HM-053).
 *
 * Daraus folgt die zweite Einschränkung, und sie ist Regel 4: **Über einen
 * Quellwechsel hinweg wird nichts gerechnet.** Die Reihe gehört dem Datenpunkt,
 * der das Zählwerk *heute* speist; wechselte die Bindung im gezeigten Zeitraum,
 * fehlt der Teil davor. Der Satz dazu steht an der Sektion, und ein Rücksprung
 * innerhalb der Reihe hat ohnehin seinen eigenen Kasten.
 *
 * **Reiter, Zählwerk und Zeitraum stehen in der Adresse** — der Reiter im Pfad,
 * die beiden anderen als `?zaehlwerk=` und `?zeitraum=`. Dieselbe Begründung
 * wie beim Gerät (HM-042): Ein Zweirichtungszähler führt Bezug und Einspeisung
 * getrennt, und „sieh dir die Einspeisung der letzten Woche an" ist ein Link
 * oder es ist eine Wegbeschreibung.
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Dialog,
  EmptyState,
  Input,
  SegmentedControl,
  Select,
  icons,
} from "@hausmeter/ui";
import { formatInstant } from "@hausmeter/i18n";
import type { ApiComponents } from "@hausmeter/sdk";
import { api } from "../lib/client.js";
import { apiErrorMessage } from "../lib/api-error.js";
import { DefinitionList } from "../components/definition-list.js";
import { HistoryChart } from "../components/history-chart.js";
import { HistoryTiles } from "../components/history-tiles.js";
import { Section } from "../components/section.js";
import { formatTimeAgo } from "../lib/format.js";
import { datapointFormatter, formatDatapointValue } from "../lib/datapoint-format.js";
import { RANGE_LABELS, rangeWindow, retentionCutsInto } from "../lib/history-range.js";
import {
  navigate,
  routePath,
  HISTORY_RANGES,
  type HistoryRange,
  type MeterTab,
  type Route,
} from "../lib/router.js";
import {
  useDatapointHistory,
  useDatapoints,
  useDatapointValues,
  useDevices,
  useGenerationAssets,
  useMeter,
  useMeteringPoints,
  useMeters,
  useSiteAreas,
} from "../lib/queries.js";
import {
  meterBadge,
  meterKindLabel,
  meterNumberText,
  registerKeyLabel,
  registerRoleLabel,
  sortRegisters,
} from "../lib/meter-labels.js";
import { useRegisterLabeler } from "../lib/register-labels.js";
import { useSiteAccess } from "../lib/site-access.js";

type Meter = ApiComponents["schemas"]["Meter"];
type MeterRegister = ApiComponents["schemas"]["MeterRegister"];
type Row = readonly [string, string];

const CLIP = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } as const;
const MONO = { fontFamily: "var(--font-mono)", fontVariantNumeric: "tabular-nums" } as const;

export function MeterDetailScreen({
  siteId,
  meterId,
  tab,
  registerId,
  range,
  siteTimeZone,
}: {
  siteId: string;
  meterId: string;
  /** Welche der beiden Ansichten — sie steht im Pfad. */
  tab: MeterTab;
  /** Welches Zählwerk die Kacheln und den Verlauf füllt. `null` = das erste. */
  registerId: string | null;
  range: HistoryRange;
  /** Zeitpunkte stehen in der Zeitzone der **Anlage**, nicht in der des Browsers. */
  siteTimeZone: string;
}) {
  const meter = useMeter(meterId);
  const labelOf = useRegisterLabeler(siteId);
  const listRoute: Route = { kind: "site", siteId, area: "meters" };

  /**
   * Ob das Stammdatenformular offen steht. **Vor jedem vorzeitigen `return`**,
   * sonst liefe der Hook nicht bei jedem Zeichnen — der Zähler kann beim ersten
   * Aufbau noch laden.
   *
   * **Kein Zustand in der Adresse.** Ein offenes Formular ist keine Ansicht,
   * sondern ein Zwischenschritt; verlinkt bekäme jemand ein Formular über einer
   * Seite, die er noch nie gesehen hat.
   */
  const [editing, setEditing] = useState(false);
  // Was die Teilung nicht erlaubt, fehlt (HM-160, Regel 10).
  const rights = useSiteAccess();

  if (meter.isPending) return <Card padding="sm">Wird geladen …</Card>;

  /**
   * **Ein Zähler, den es nicht gibt, ist keine leere Seite.** Eine fremde oder
   * gelöschte Kennung ergibt `404`; die Seite sagt das und führt zurück, statt
   * mit „wird geladen" hängen zu bleiben.
   */
  if (meter.isError || meter.data === undefined) {
    return (
      <Card>
        <EmptyState title="Zähler nicht gefunden" icon={<icons.IconGauge size={30} />}>
          Diesen Zähler gibt es an dieser Anlage nicht — vielleicht wurde er entfernt oder gehört
          zu einer anderen.
          <div style={{ marginTop: "var(--sp-3)" }}>
            <Button variant="secondary" size="sm" onClick={() => navigate(listRoute)}>
              Zurück zu den Zählern
            </Button>
          </div>
        </EmptyState>
      </Card>
    );
  }

  /**
   * **Ein Zähler gehört genau einer Anlage** (Befund R-1). Steht in der Adresse
   * eine andere, öffnete die Seite ihn trotzdem — unter der Kopfzeile der falschen
   * Anlage, mit deren Messkonzept als Wörterbuch für die Zählwerke. Dieselbe
   * Antwort wie am Gerät und an der Erzeugungsanlage: nicht stillschweigend
   * umschalten, sondern sagen, wo er steht (Regel 9).
   */
  if (meter.data.siteId !== siteId) {
    return (
      <Card>
        <EmptyState
          title="Dieser Zähler gehört zu einer anderen Anlage"
          icon={<icons.IconGauge size={30} />}
          size="md"
          action={
            <Button variant="secondary" onClick={() => navigate(listRoute)}>
              Zurück zu den Zählern
            </Button>
          }
        >
          Wechsle oben die Anlage, um ihn zu sehen. Ein Zähler steht immer bei der Anlage, an der
          er gemessen wird.
        </EmptyState>
      </Card>
    );
  }

  const data = meter.data;
  const badge = meterBadge(data);
  const registers = sortRegisters(data.registers);
  /* Was ein Zählwerk hier zählt — „Verbrauch" statt „Bezug" am Wohnungszähler,
     wenn das Messkonzept es sagt (HM-090). */
  const registerLabel = (role: MeterRegister["role"]): string => labelOf(role, data.meteringPointId);

  /**
   * **Ein Zählwerk aus der Adresse, das es an diesem Zähler nicht gibt, fällt
   * auf das erste** — es wird nicht abgewiesen. Der Parameter stellt die
   * Ansicht nur ein; was sie zeigt, steht im Pfad und bleibt gültig. Ein Link
   * von gestern, dessen Zählwerk seither entfernt wurde, führt damit auf den
   * Zähler und nicht auf eine leere Fläche (Regel 9 gilt dem Pfad, nicht der
   * Einstellung).
   */
  const selected = registers.find((entry) => entry.id === registerId) ?? registers[0] ?? null;

  /** Dieselbe Adresse mit anderer Einstellung — für Reiter und beide Umschalter. */
  const viewRoute = (next: {
    tab?: MeterTab;
    register?: string | null;
    range?: HistoryRange;
  }): Route => ({
    kind: "meter",
    siteId,
    meterId,
    tab: next.tab ?? tab,
    register: next.register === undefined ? registerId : next.register,
    range: next.range ?? range,
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 1100 }}>
      {/**
       * **Name und Fakten stehen in der Kopfleiste des Rahmens** (HM-072) — der
       * Entwurf legt sie dorthin, und die Seite beginnt ohne eigene Kopfzeile.
       * Sie trug bis hierher einen Namen über einer Faktenzeile, also genau die
       * Form, die die Kopfleiste ohnehin hat: zweimal untereinander, einmal mit
       * „Zähler" und einmal mit dem Namen des Zählers.
       *
       * Der Einwand aus HM-068 dagegen — „das ist eine Änderung am Rahmen und
       * nicht an dieser Seite" — galt gegen den damaligen Umfang: Die Kopfzeile
       * trug noch das Abzeichen und „Gerät öffnen". Seit die ausgezogen sind
       * (HM-071), blieb eine Zeile übrig, die nichts tat, was der Rahmen nicht
       * besser kann. **Eine Entscheidung, die auf einem Umfang beruht, gilt nur
       * so lange wie der Umfang** — dieselbe Wendung wie bei den Reitern selbst.
       */}

      {/**
       * **Der einzige Hinweis dieser Seite, und er lässt sich abstellen.** Er
       * steht nur an einem Zähler, dem wirklich etwas fehlt — die Zählernummer
       * —, und verschwindet, sobald sie eingetragen ist. Ein Kasten, der immer
       * da ist, entwertet die daneben (HM-042).
       *
       * **Über den Reitern und nicht in einem von ihnen:** Was fehlt, gilt dem
       * Zähler und nicht einer seiner beiden Ansichten. Läge er unter
       * „Übersicht", verschwände er beim Umschalten — und wer gerade
       * einrichtet, ist genau derjenige, den er angeht.
       *
       * **Und jetzt mit dem Knopf des Entwurfs.** Er fehlte, weil derselbe Weg
       * eine Handbreit darüber in der Kopfzeile stand — die gibt es nicht mehr,
       * und damit ist dies die einzige Stelle, an der die Übersicht nach der
       * Nummer fragt. **Die drei Punkte sind eine Zusage** (HM-042): Es kommt
       * ein Formular, hier als Dialog — man bleibt stehen, wo man war.
       */}
      {data.meterNumber === null && data.status === "draft" && (
        <Alert
          tone="warning"
          action={
            rights.canWrite ? (
              <Button
                variant="secondary"
                size="sm"
                style={{ whiteSpace: "nowrap" }}
                onClick={() => setEditing(true)}
              >
                Nummer eintragen …
              </Button>
            ) : undefined
          }
        >
          <strong>Zählernummer fehlt.</strong> {badge.explanation}
        </Alert>
      )}

      {/* **Ein Formular, zwei Einstiege** — der Kasten oben und „Ändern" in den
          Stammdaten. Zwei Fassungen desselben Formulars liefen beim nächsten
          Feld auseinander, und die eine sähe aus, als könne sie mehr. */}
      {rights.canWrite && (
        <MeterEditDialog meter={data} open={editing} onClose={() => setEditing(false)} />
      )}

      {/**
       * **Ansicht und Zählwerk in einer Zeile**, wie im Entwurf: Zwei
       * Umschalter untereinander kosten eine Zeilenhöhe und lesen sich als
       * Hierarchie, die es nicht gibt — der Reiter wählt die Ansicht, der
       * Umschalter daneben, worüber sie spricht.
       *
       * **Und der zweite steht nur an der Übersicht.** Unter „Einstellungen"
       * stehen alle Zählwerke gleichzeitig; ein Umschalter, der dort nichts
       * bewirkt, sähe aus wie eine Filterung, die nicht greift.
       */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "var(--sp-3)",
        }}
      >
        {/**
         * **Das Abzeichen steht neben den Reitern, nicht in der Kopfzeile**
         * (überarbeiteter Entwurf). Es sagt etwas über den Zähler und nicht
         * über eine seiner beiden Ansichten — und trotzdem gehört es hierher:
         * In der Kopfzeile stand es zwischen dem Namen und „Bauart · Nummer ·
         * Zählwerke", also mitten in einer Zeile, die ohnehin schon vier
         * Angaben trägt und als Erste umbricht. Hier steht es allein an einer
         * Leiste, die sonst nichts trägt.
         *
         * **Nur, wenn es etwas sagt.** „Aktiv" ist der Normalfall und braucht
         * keines.
         */}
        <div style={{ display: "flex", alignItems: "center", gap: "var(--sp-3)" }}>
          <MeterTabs current={tab} viewRoute={viewRoute} />
          {data.status !== "active" && (
            <Badge tone={badge.tone} title={badge.explanation}>
              {badge.label}
            </Badge>
          )}
        </div>
        {tab === "overview" && selected !== null && registers.length > 1 && (
          /* Sieben Zählwerke (ein Wärmemengenzähler, HM-192) sind breiter als ein
             Fenster von 960 px: Die Umschaltleiste scrollt in sich, statt die ganze
             Seite waagerecht zu schieben. */
          <div style={{ maxWidth: "100%", minWidth: 0, overflowX: "auto" }}>
            <SegmentedControl
              label="Zählwerk"
              size="sm"
              value={selected.id}
              onChange={(id) => navigate(viewRoute({ register: id }))}
              options={registers.map((register) => ({
                value: register.id,
                label: registerLabel(register.role),
              }))}
            />
          </div>
        )}
      </div>

      {tab === "overview" && (
        <>
          {selected === null ? (
            /* **Ohne Zählwerk hat die Übersicht nichts zu zeigen** — und
               verspricht auch nichts: Ein Zählwerk lässt sich hier nicht
               anlegen, es entsteht mit dem Zähler (HM-058). */
            <Card>
              <EmptyState title="Kein Zählwerk" icon={<icons.IconGauge size={30} />}>
                Dieser Zähler führt kein Zählwerk. Ohne Zählwerk gibt es keinen Stand, aus dem sich
                ein Verbrauch bilden ließe — das ist die Grenze zum Sensor.
              </EmptyState>
            </Card>
          ) : (
            <MeasurementSection
              selected={selected}
              virtual={data.virtual}
              registerLabel={registerLabel}
              range={range}
              timeZone={siteTimeZone}
              siteId={siteId}
              onSelectRange={(next) => navigate(viewRoute({ range: next }))}
            />
          )}

          {/* **Ohne Zählwerk keine Quellzeile.** „Quelle: keine" unter „Kein
              Zählwerk" beantwortete eine Frage, die sich gar nicht stellt —
              gebunden wird am Zählwerk, nicht am Zähler (HM-045). */}
          {selected !== null && (
            <SourceSummary
              siteId={siteId}
              registers={registers}
              settingsRoute={viewRoute({ tab: "settings" })}
              virtual={data.virtual}
            />
          )}

        </>
      )}

      {tab === "settings" && (
        <>
          <RegisterSection
              registerLabel={registerLabel}
            siteId={siteId}
            meter={data}
            registers={registers}
            timeZone={siteTimeZone}
          />
          <FactsSection
            meter={data}
            timeZone={siteTimeZone}
            onEdit={rights.canWrite ? () => setEditing(true) : undefined}
          />
          {rights.canDelete && <DangerSection meter={data} listRoute={listRoute} />}
        </>
      )}
    </div>
  );
}

/**
 * Die Reiterleiste.
 *
 * **Echte Links und keine Knöpfe** — dieselbe Begründung wie am Gerät
 * (HM-042): Jeder Reiter hat eine Adresse, und ein `<button>` davor wäre weder
 * im neuen Tab zu öffnen noch zu kopieren. Strg-, Cmd- und Mittelklick gehen
 * deshalb an den Browser weiter.
 *
 * `aria-current="page"` statt `role="tab"`: Was hier steht, sind Verweise auf
 * zwei Adressen und keine Reiter eines Widgets.
 */
function MeterTabs({
  current,
  viewRoute,
}: {
  current: MeterTab;
  viewRoute: (next: { tab?: MeterTab }) => Route;
}) {
  const tabs: readonly { tab: MeterTab; label: string }[] = [
    { tab: "overview", label: "Übersicht" },
    { tab: "settings", label: "Einstellungen" },
  ];

  return (
    <nav
      aria-label="Ansichten dieses Zählers"
      style={{
        display: "flex",
        gap: 4,
        padding: 4,
        flexWrap: "wrap",
        background: "var(--surface-sunken)",
        borderRadius: "var(--radius-md)",
      }}
    >
      {tabs.map(({ tab, label }) => {
        const route = viewRoute({ tab });
        const active = tab === current;

        return (
          <a
            key={tab}
            href={routePath(route)}
            aria-current={active ? "page" : undefined}
            onClick={(event) => {
              if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
              event.preventDefault();
              navigate(route);
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              padding: "7px 14px",
              borderRadius: "var(--radius-sm)",
              textDecoration: "none",
              fontFamily: "var(--font-body)",
              fontSize: 13.5,
              fontWeight: active ? 700 : 500,
              background: active ? "var(--surface-card)" : "transparent",
              boxShadow: active ? "var(--shadow-xs)" : "none",
              color: active ? "var(--text-strong)" : "var(--text-muted)",
            }}
          >
            {label}
          </a>
        );
      })}
    </nav>
  );
}

/**
 * Die eine Zeile, die sagt, woher der Wert kommt — und der Weg dorthin, wo man
 * es ändert.
 *
 * **Sie ersetzt die Zählwerksliste auf der Übersicht, sie wiederholt sie
 * nicht.** Die Frage an dieser Stelle lautet nicht „welcher Datenpunkt speist
 * welches Zählwerk", sondern „kommt das hier aus einem Gerät". Alles Weitere
 * steht einen Reiter weiter — und ohne diese Zeile wüsste niemand, dass es
 * dort steht.
 */
function SourceSummary({
  siteId,
  registers,
  settingsRoute,
  virtual = false,
}: {
  siteId: string;
  registers: readonly MeterRegister[];
  settingsRoute: Route;
  /** Ein virtueller Zähler (HM-123) nennt seine Quellen als Zahl — es sind absichtlich mehrere. */
  virtual?: boolean;
}) {
  const bound = registers.filter((register) => register.source !== null);
  const sourceCount = registers.reduce((sum, register) => sum + register.sources.length, 0);
  /* Zwei Zählwerke aus zwei Geräten sind möglich (HM-045) — dann wird hier
     keines beim Namen genannt, sondern gezählt. Ein Name, der nur für die
     Hälfte gilt, ist die falschere Auskunft als eine Zahl. */
  const devices = new Set(bound.map((register) => register.source!.deviceName));
  const sole = devices.size === 1 ? [...devices][0]! : null;

  /**
   * **„Gerät öffnen" steht seit dem überarbeiteten Entwurf hier** und nicht
   * mehr in der Kopfzeile — und das ist die richtige Stelle: Der Satz daneben
   * nennt das Gerät beim Namen, der Knopf führt zu ebendiesem. In der Kopfzeile
   * stand er eine Handbreit von jeder Erwähnung entfernt, neben „Ändern", mit
   * dem er nichts zu tun hat.
   *
   * **Nur bei genau einem Gerät.** Speisen zwei Geräte zwei Zählwerke, führte
   * ein Knopf ohne Namen zu einem von beiden, und welches, wüsste niemand; der
   * Weg geht dann über die Zählwerksliste, wo steht, welches welches ist —
   * dieselbe Überlegung wie beim Namen im Satz davor.
   */
  const deviceIds = new Set(bound.map((register) => register.source!.deviceId));
  const deviceRoute: Route | null =
    deviceIds.size === 1
      ? { kind: "device", siteId, deviceId: [...deviceIds][0]!, tab: "overview" }
      : null;

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "var(--sp-3)",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "11px var(--sp-4)",
        borderRadius: "var(--radius-md)",
        background: "var(--surface-inset)",
        border: "1px solid var(--border-subtle)",
      }}
    >
      <span style={{ minWidth: 0, fontSize: 12.5, lineHeight: 1.55, color: "var(--text-muted)" }}>
        {virtual ? (
          <>
            Quellen:{" "}
            <strong style={{ color: "var(--text-strong)" }}>
              {sourceCount === 0 ? "keine" : sourceCount === 1 ? "eine Erzeugungsanlage" : `${sourceCount} Erzeugungsanlagen`}
            </strong>
            {" · "}der Stand ist ihre Summe
          </>
        ) : bound.length === 0 ? (
          /* **„Keine Quelle" ist grau und keine Warnung.** Nach einem
             Gerätetausch ist genau das der Normalzustand, bis die nächste
             Bindung gesetzt ist — und ein abgelöster Zähler bekommt nie wieder
             eine. */
          <>Quelle: keine — diesem Zähler ist derzeit kein Wert zugeordnet.</>
        ) : (
          <>
            Quelle:{" "}
            {sole === null ? (
              <strong style={{ color: "var(--text-strong)" }}>{devices.size} Geräte</strong>
            ) : (
              <strong style={{ color: "var(--text-strong)" }}>{sole}</strong>
            )}
            {" · "}
            {bound.length === registers.length
              ? registers.length === 1
                ? "1 Zählwerk gebunden"
                : `alle ${registers.length} Zählwerke gebunden`
              : `${bound.length} von ${registers.length} Zählwerken gebunden`}
          </>
        )}
      </span>

      <span style={{ display: "flex", gap: "var(--sp-2)", flexWrap: "wrap" }}>
        {deviceRoute !== null && (
          <Button
            variant="secondary"
            size="sm"
            style={{ whiteSpace: "nowrap" }}
            onClick={() => navigate(deviceRoute)}
          >
            Gerät öffnen
          </Button>
        )}
        <Button
          variant="secondary"
          size="sm"
          style={{ whiteSpace: "nowrap" }}
          onClick={() => navigate(settingsRoute)}
        >
          Zählwerke und Quellen
        </Button>
      </span>
    </div>
  );
}
/* --------------------------------------------------------------------------
 * Über beiden Reitern · Die Stammdaten ändern
 * ----------------------------------------------------------------------- */

/**
 * Bezeichnung und Zählernummer ändern — als Dialog.
 *
 * **Er ist das, was von der Kopfzeile übrig geblieben ist** (HM-072). Zurück,
 * Name und Faktenzeile hat die Kopfleiste des Rahmens übernommen; das Abzeichen
 * steht an der Reiterleiste, „Gerät öffnen" in der Quellzeile (HM-071). Was
 * eine eigene Zeile brauchte, war am Ende dieses Formular.
 *
 * **Ein Dialog und keine Karte, und das folgt aus den zwei Einstiegen.** Er
 * öffnet aus dem Hinweis auf der **Übersicht** („Nummer eintragen …") und aus
 * den **Stammdaten** unter „Einstellungen" („Ändern"). Beide Wege gehören
 * dorthin: Die Übersicht ist die Seite, auf der man landet und den Mangel sieht;
 * die Stammdaten sind der Ort, an dem die Angabe steht. Eine Karte müsste sich
 * für einen der beiden Reiter entscheiden — und der andere hätte dann einen
 * Knopf, der die Ansicht wechselt, statt zu tun, was er verspricht. **Die drei
 * Punkte hinter „Nummer eintragen …" sagen genau das zu** (HM-042).
 *
 * **Die Zählernummer lässt sich berichtigen, aber nicht entfernen.** An ihr
 * hängt der Übergang von „Entwurf" auf „Aktiv", und den gibt es in der
 * Gegenrichtung nicht (`canTransitionMeter`).
 */
function MeterEditDialog({
  meter,
  open,
  onClose,
}: {
  meter: Meter;
  open: boolean;
  onClose: () => void;
}) {
  const client = useQueryClient();

  /**
   * **Die Wohneinheit hängt an der Messstelle, nicht am Zähler** (HM-079) —
   * ein Zählerwechsel darf sie nicht verlieren. Das Formular zeigt sie
   * trotzdem hier: Wer vor dem Zählerschrank steht, liest die Wohnung vom
   * Zähler ab. Gespeichert wird sie deshalb über `PATCH /metering-points`,
   * getrennt vom Zähler, und der Satz am Feld sagt das.
   */
  const areas = useSiteAreas(meter.siteId);
  const points = useMeteringPoints(meter.siteId);
  const point = (points.data?.items ?? []).find((entry) => entry.id === meter.meteringPointId);
  const currentAreaId = point?.siteAreaId ?? null;
  const areaChoices = areas.data?.items ?? [];

  const [label, setLabel] = useState(meter.label);
  const [meterNumber, setMeterNumber] = useState(meter.meterNumber ?? "");
  const [builtIntoDevice, setBuiltIntoDevice] = useState(meter.builtIntoDevice);
  const [areaId, setAreaId] = useState<string>(currentAreaId ?? "");
  const [error, setError] = useState<string | null>(null);

  /**
   * **Beim Öffnen wird zurückgesetzt.** Wer abbricht, tippt beim nächsten Mal
   * nicht auf seinem verworfenen Versuch weiter — und ein Zähler, der sich
   * zwischenzeitlich geändert hat (die Seite lädt nach), zeigt seinen heutigen
   * Stand statt des Stands beim ersten Aufbau der Seite.
   */
  useEffect(() => {
    if (!open) return;
    setLabel(meter.label);
    setMeterNumber(meter.meterNumber ?? "");
    setBuiltIntoDevice(meter.builtIntoDevice);
    setAreaId(currentAreaId ?? "");
    setError(null);
  }, [open, meter.label, meter.meterNumber, meter.builtIntoDevice, currentAreaId]);

  const save = useMutation({
    mutationFn: async () => {
      const chosenArea = areaId.length > 0 ? areaId : null;
      if (chosenArea !== currentAreaId) {
        const { error: pointFailure } = await api.PATCH("/metering-points/{meteringPointId}", {
          params: { path: { meteringPointId: meter.meteringPointId } },
          body: { siteAreaId: chosenArea },
        });
        if (pointFailure) {
          throw new Error(
            apiErrorMessage(pointFailure) ?? "Die Wohneinheit ließ sich nicht zuordnen.",
          );
        }
      }

      const { data, error: failure } = await api.PATCH("/meters/{meterId}", {
        params: { path: { meterId: meter.id } },
        body: {
          ...(label.trim() !== meter.label ? { label: label.trim() } : {}),
          ...(meterNumber.trim().length > 0 && meterNumber.trim() !== meter.meterNumber
            ? { meterNumber: meterNumber.trim() }
            : {}),
          ...(builtIntoDevice !== meter.builtIntoDevice ? { builtIntoDevice } : {}),
        },
      });
      if (failure || !data) {
        throw new Error(apiErrorMessage(failure) ?? "Die Änderung ließ sich nicht speichern.");
      }
      return data;
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["meters"] });
      await client.invalidateQueries({ queryKey: ["metering-points"] });
      await client.invalidateQueries({ queryKey: ["site-areas"] });
      onClose();
    },
    onError: (failure: Error) => setError(failure.message),
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Stammdaten ändern"
      description="Bezeichnung, Zählernummer, Messwerk und Wohneinheit. Alles andere folgt aus der Rolle der Zählwerke oder aus dem, was das Gerät meldet."
      footer={
        <>
          <Button
            type="submit"
            form="meter-stammdaten"
            size="sm"
            disabled={save.isPending}
          >
            {save.isPending ? "Wird gespeichert …" : "Speichern"}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Abbrechen
          </Button>
        </>
      }
    >
      {/* `form` am Knopf im Fuß: Der Absendeknopf steht außerhalb des
          Formulars, und ohne die Zuordnung wäre er keiner. */}
      <form
        id="meter-stammdaten"
        style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          save.mutate();
        }}
      >
        <Input
          label="Bezeichnung"
          value={label}
          maxLength={120}
          onChange={(event) => setLabel(event.target.value)}
        />
        {/* Ein virtueller Zähler (HM-123) hat weder Typenschild noch Messwerk —
            beide Felder fragten nach etwas, das es nicht gibt (Regel 10). */}
        {!meter.virtual && (
          <>
            <Input
              label="Zählernummer"
              value={meterNumber}
              maxLength={64}
              hint={
                builtIntoDevice
                  ? "Ein Messwerk im Gerät hat keine — das Feld darf leer bleiben."
                  : "Vom Typenschild. Sie lässt sich berichtigen, aber nicht wieder entfernen."
              }
              onChange={(event) => setMeterNumber(event.target.value)}
            />
            {/* HM-112: Ohne Typenschild keine Nummer — und ohne das Merkmal bliebe der
                Zähler ein Entwurf, dessen Hinweis sich nie abstellen lässt. Zurück in den
                Entwurf führt das Abwählen nicht (`canTransitionMeter`). */}
            <Checkbox
              checked={builtIntoDevice}
              onChange={setBuiltIntoDevice}
              label="Messwerk in einem Gerät, ohne Typenschild (Wallbox, Wärmepumpe, Wechselrichter)"
            />
          </>
        )}
        {/* **Nur, wenn es Wohneinheiten gibt** — ein Auswahlfeld mit dem einen
            Eintrag „keine" fragte nach etwas, das es an dieser Anlage nicht
            gibt (Regel 10). Die Zuordnung gilt der Messstelle; der Hinweis
            sagt es, damit ein späterer Zählerwechsel niemanden wundert. */}
        {areaChoices.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <Select
              label="Wohneinheit"
              value={areaId}
              onChange={(event) => setAreaId(event.target.value)}
              options={[
                { value: "", label: "Keine Wohneinheit" },
                ...areaChoices.map((area) => ({ value: area.id, label: area.name })),
              ]}
            />
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
              Gilt für die Messstelle — ein späterer Zähler an derselben Stelle erbt sie.
            </span>
          </div>
        )}

        {error !== null && <Alert tone="danger">{error}</Alert>}
      </form>
    </Dialog>
  );
}

/* --------------------------------------------------------------------------
 * Übersicht · Was steht drauf?
 * ----------------------------------------------------------------------- */

/**
 * Kennzahlen und Verlauf des gewählten Zählwerks.
 *
 * **Das ist der Teil, der der Seite gefehlt hat.** Sie zeigte bis HM-066
 * keinen einzigen Wert — man richtete einen Zähler ein und musste danach über
 * das Gerät und dessen Datenpunkt gehen, um zu sehen, ob etwas ankommt.
 *
 * **Gerechnet wird nichts hier**: Kacheln und Diagramm bekommen dieselbe Antwort
 * des Servers wie die Datenpunktseite und dieselben Komponenten dafür
 * (`HistoryTiles`, `HistoryChart`). Zwei Fassungen derselben Auswertung liefen
 * beim nächsten Feld auseinander, und die eine sähe aus, als hätte sie mehr
 * Daten.
 *
 * **Und die Kurve trägt `colors.datapoint`, nicht die Farbe einer Energiegröße**
 * (ADR 0013) — obwohl das Zählwerk eine Rolle hat und die Versuchung groß ist,
 * daraus eine Größe zu machen. Sie folgt aber nicht aus der Rolle allein: Der
 * Bezug eines **Netzzählers** ist Netzbezug, der Bezug eines **Unterzählers**
 * an der Wallbox ist Verbrauch. Welche Größe gemeint ist, entscheidet erst die
 * Stellung des Zählers im Messkonzept (HM-012). Bis dahin wird sie nicht
 * geraten (Regel 9) — eine Wallbox in Netzbezugs-Orange wäre eine Aussage, die
 * niemand gemeint hat.
 */
function MeasurementSection({
  selected,
  virtual,
  registerLabel,
  range,
  timeZone,
  siteId,
  onSelectRange,
}: {
  selected: MeterRegister;
  /** Ein virtueller Zähler (HM-123): Sein Verlauf ist keine Rohwertreihe eines Datenpunkts. */
  virtual: boolean;
  registerLabel: (role: MeterRegister["role"]) => string;
  range: HistoryRange;
  timeZone: string;
  siteId: string;
  onSelectRange: (range: HistoryRange) => void;
}) {
  const source = virtual ? null : selected.source;

  /**
   * **Einmal je Zeitraum gebildet, nicht bei jedem Zeichnen.** Sonst trüge der
   * Abfrageschlüssel jedes Mal ein anderes `to`, und die Reihe würde endlos
   * nachgeladen.
   */
  const window = useMemo(() => rangeWindow(range), [range]);

  const datapoints = useDatapoints(source?.deviceId ?? null);
  const history = useDatapointHistory(
    source?.deviceId ?? null,
    source?.datapointId ?? null,
    window,
  );

  const point =
    source === null
      ? null
      : (datapoints.data?.items.find((entry) => entry.id === source.datapointId) ?? null);

  /**
   * **Die Überschrift nennt das Zählwerk.** Der Umschalter steht seit HM-068
   * eine Zeile höher, neben den Reitern; ohne den Namen hier stünde „Verlauf"
   * über einer Kurve, deren Zugehörigkeit man sich aus einem Umschalter am
   * anderen Ende der Seite zusammensuchen müsste.
   */
  return (
    <Section
      title={`Verlauf · ${registerLabel(selected.role)}`}
      /**
       * **Der Satz ist keine Fußnote, sondern die Einordnung der ganzen
       * Sektion.** Was hier steht, sind die Rohwerte *eines* Datenpunkts — die
       * Reihe der Messstelle über ihre Zählerwechsel hinweg gibt es erst mit
       * HM-008, und sie beantwortet eine andere Frage.
       */
      subtitle="Was das Gerät an diesem Zähler gemeldet hat."
      action={
        source === null ? undefined : (
          <SegmentedControl
            label="Zeitraum"
            size="sm"
            value={range}
            onChange={onSelectRange}
            options={HISTORY_RANGES.map((id) => ({ value: id, label: RANGE_LABELS[id] }))}
          />
        )
      }
    >
      {/**
       * **„Keine Quelle" ist grau und keine Warnung.** Nach einem Gerätetausch
       * ist genau das der Normalzustand, bis die nächste Bindung gesetzt ist —
       * und ein abgelöster Zähler bekommt nie wieder eine.
       */}
      {/**
       * **Der Stand eines virtuellen Zählers ist die Summe seiner Quellen**
       * (HM-123) — die Rohwerte einer einzelnen Quelle wären hier die falsche
       * Kurve. Die Summe steht als Reihe der Messstelle unter „Strom &
       * Erzeugung"; die Quellen führen zu ihrem eigenen Verlauf.
       */}
      {virtual && (
        <Card>
          <EmptyState title="Ein virtueller Erzeugungszähler" icon={<icons.IconSun size={30} />}>
            Sein Stand ist die Summe aus {selected.sources.length === 1 ? "einer Quelle" : `${selected.sources.length} Quellen`} —
            alle Viertelstunde gerechnet, nicht abgelesen. Was daraus wird, zeigt „Strom &amp;
            Erzeugung"; den Verlauf jeder einzelnen Quelle zeigt ein Klick auf sie:
            <span style={{ display: "block", marginTop: 8 }}>
              {selected.sources.map((entry) => (
                <a
                  key={entry.datapointId}
                  href={routePath({ kind: "datapoint", siteId, deviceId: entry.deviceId, datapointId: entry.datapointId, range })}
                  onClick={(event) => {
                    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
                    event.preventDefault();
                    navigate({ kind: "datapoint", siteId, deviceId: entry.deviceId, datapointId: entry.datapointId, range });
                  }}
                  style={{ display: "block", color: "var(--text-strong)", fontWeight: 600 }}
                >
                  {entry.deviceName} · {entry.datapointName}
                  {entry.kind === "power" ? " (Leistung, integriert)" : ""}
                </a>
              ))}
            </span>
          </EmptyState>
        </Card>
      )}

      {!virtual && source === null && (
        <Card>
          <EmptyState title="Dieses Zählwerk hat keine Quelle" icon={<icons.IconGauge size={30} />}>
            Es bekommt gerade keine Werte, deshalb gibt es hier nichts zu zeigen. Eine Quelle wird
            unter <strong>Einstellungen</strong> gesetzt — erst das Gerät, dann der Wert.
          </EmptyState>
        </Card>
      )}

      {source !== null && (datapoints.isPending || history.isPending) && (
        <Card padding="sm">Der Verlauf wird geladen …</Card>
      )}

      {/**
       * **Ein gebundener Datenpunkt, den der Katalog nicht mehr führt, ist ein
       * Zustand und kein Ladefehler.** Das Gerät kann seinen Katalog geändert
       * haben; die Bindung steht dann auf etwas, das es nicht mehr anbietet.
       */}
      {source !== null && !datapoints.isPending && point === null && (
        <Card>
          <EmptyState title="Das Gerät meldet diesen Wert nicht mehr" icon={<icons.IconGauge size={30} />}>
            <span style={MONO}>{source.datapointKey}</span> steht nicht mehr unter den Werten von{" "}
            {source.deviceName}. Solange das so ist, kommt an diesem Zählwerk nichts an — die
            Bindung selbst bleibt bestehen.
          </EmptyState>
        </Card>
      )}

      {source !== null && point !== null && !history.isPending && (
        <MeasurementBody
          history={history.data}
          failed={history.isError}
          point={point}
          range={range}
          timeZone={timeZone}
          route={{
            kind: "datapoint",
            siteId,
            deviceId: source.deviceId,
            datapointId: source.datapointId,
            range,
          }}
        />
      )}
    </Section>
  );
}

/** Kasten, Kacheln, großer Wert und Diagramm — sobald eine Reihe vorliegt. */
function MeasurementBody({
  history,
  failed,
  point,
  range,
  timeZone,
  route,
}: {
  history: ApiComponents["schemas"]["DatapointHistoryResponse"] | undefined;
  failed: boolean;
  point: ApiComponents["schemas"]["Datapoint"];
  range: HistoryRange;
  timeZone: string;
  /** Der Weg zum vollen Verlauf dieses Datenpunkts, samt Tabelle. */
  route: Route;
}) {
  if (failed || history === undefined) {
    return (
      <Card>
        <EmptyState title="Der Verlauf ist nicht abrufbar" icon={<icons.IconGauge size={30} />}>
          Die Reihe konnte nicht geladen werden. Die Bindung steht; was fehlt, sind die Werte dazu.
        </EmptyState>
      </Card>
    );
  }

  const stats = history.statistics;
  /* Die Größenordnung der ganzen Reihe kommt aus ihrem größten Betrag — sonst
     stünde an der einen Achsenkante „1,1 MW" und an der nächsten „980 kW". */
  const formatter = datapointFormatter(
    point,
    Math.max(
      ...history.points.flatMap((entry) =>
        history.aggregate === "mean"
          ? [Math.abs(entry.v), Math.abs(entry.min), Math.abs(entry.max)]
          : [Math.abs(entry.v)],
      ),
      0,
    ),
  );

  return (
    <>
      {/**
       * **Ein Rücksprung ist die eine Auffälligkeit, die eine Erklärung
       * braucht** (Regel 4). An einem Zähler wiegt sie doppelt: Hier steht die
       * Abrechnungsgrundlage, und eine Differenz über einen Wechsel hinweg wäre
       * keine Verbrauchsmenge.
       */}
      {stats?.counterReset === true && (
        <Alert tone="warning">
          Der Wert ist in diesem Zeitraum <strong>zurückgesprungen</strong>. Das geschieht, wenn das
          Gerät einen anderen Zähler liest oder zurückgesetzt wurde. Ein Zuwachs wird deshalb nicht
          gebildet — und ist es wirklich ein neuer Zähler, gehört das als{" "}
          <strong>Zählerwechsel</strong> festgehalten und nicht als Sprung in einer Reihe.
        </Alert>
      )}

      {retentionCutsInto(history, history.retainedFrom) && (
        <Alert tone="info">
          Einzelne Meldungen werden nur begrenzt aufbewahrt. Vor{" "}
          {formatInstant(new Date(history.retainedFrom ?? ""), timeZone, "de-DE", "date")} liegen
          deshalb keine mehr vor — dort fehlt nichts. Die Zählerstände für Verbrauch und Abrechnung
          bleiben davon unberührt.
        </Alert>
      )}

      {stats !== null && <HistoryTiles history={history} stats={stats} point={point} />}

      <Card>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "baseline",
            gap: 8,
            marginBottom: "var(--sp-4)",
          }}
        >
          {stats === null ? (
            <span style={{ fontSize: 14, color: "var(--text-muted)" }}>
              In diesen {RANGE_LABELS[range]} ist kein Wert eingegangen.
            </span>
          ) : (
            <>
              {/* Der große Wert steht in seiner eigenen Größenordnung, nicht in
                  der der Reihe: Aus 168 W würde sonst „0,2 kW", weil irgendwo
                  eine Spitze von 2,8 kW liegt. */}
              <span
                style={{
                  ...MONO,
                  fontSize: 30,
                  fontWeight: 700,
                  lineHeight: 1,
                  color: "var(--text-strong)",
                }}
              >
                {formatDatapointValue(stats.last.v, point)}
              </span>
              {/* `nowrap`: Ohne das bricht „Stand vor 2 Minuten" auf einem
                  Telefon in drei Zeilen neben der Zahl, statt unter sie zu
                  rücken. */}
              <span
                style={{ fontSize: 12.5, whiteSpace: "nowrap", color: "var(--text-faint)" }}
              >
                Stand {formatTimeAgo(stats.last.t)}
              </span>
            </>
          )}
        </div>

        <div style={{ marginBottom: -14 }}>
          <HistoryChart history={history} point={point} formatter={formatter} timeZone={timeZone} />
        </div>
      </Card>

      {/**
       * **Der Weg zum vollen Verlauf.** Die Datenpunktseite trägt zusätzlich die
       * Tabelle der zuletzt gemeldeten Werte; sie hier zu wiederholen hieße,
       * dieselbe Reihe zweimal zu zeigen — einmal mit und einmal ohne Zähler
       * darum herum.
       */}
      <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, color: "var(--text-muted)" }}>
        Die einzelnen Meldungen samt Zeitpunkt stehen bei{" "}
        <a
          href={routePath(route)}
          onClick={(event) => {
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
            event.preventDefault();
            navigate(route);
          }}
          style={{ color: "var(--hm-green-700)", fontWeight: 600 }}
        >
          „{point.name}“ am Gerät
        </a>
        .
      </p>
    </>
  );
}

/* --------------------------------------------------------------------------
 * Einstellungen · Woher kommt das?
 * ----------------------------------------------------------------------- */

/**
 * Die Zählwerke samt ihren Quellen.
 *
 * **Am Zählwerk und nicht am Zähler**: Ein Zweirichtungszähler führt Bezug und
 * Einspeisung getrennt, und sie können aus verschiedenen Datenpunkten kommen
 * (ADR 0020, HM-045).
 *
 * Seit HM-066 im Aufbau des Entwurfs: je Zählwerk eine abgesetzte Kopfzeile mit
 * Rolle und Kennzahl, darunter die Quellzeile mit dem zuletzt eingegangenen
 * Wert. **Der Wert steht dort, weil das die Frage an einer Bindung ist** —
 * nicht „welcher Datenpunkt", sondern „kommt darüber etwas an".
 */
function RegisterSection({
  siteId,
  meter,
  registers,
  timeZone,
  registerLabel,
}: {
  registerLabel: (role: MeterRegister["role"]) => string;
  siteId: string;
  meter: Meter;
  registers: readonly MeterRegister[];
  timeZone: string;
}) {
  const rights = useSiteAccess();
  /* Genau ein Gerät hinter allen Bindungen — sonst keines. */
  const deviceIds = new Set(
    registers
      .map((register) => register.source?.deviceId)
      .filter((id): id is string => id !== undefined),
  );
  const deviceRoute: Route | null =
    deviceIds.size === 1
      ? { kind: "device", siteId, deviceId: [...deviceIds][0]!, tab: "overview" }
      : null;

  return (
    <Section
      title="Zählwerke und ihre Quellen"
      subtitle="Einheit, Richtung und Monotonie folgen aus der Rolle — sie werden nicht eingestellt."
    >
      <Card padding="none">
        {registers.length === 0 ? (
          <EmptyState title="Keine Zählwerke" icon={<icons.IconGauge size={30} />}>
            Dieser Zähler führt kein Zählwerk. Ohne Zählwerk gibt es keinen Stand, aus dem sich ein
            Verbrauch bilden ließe — das ist die Grenze zum Sensor.
          </EmptyState>
        ) : (
          registers.map((register, index) => (
            <RegisterRow
              key={register.id}
              siteId={siteId}
              meter={meter}
              register={register}
              label={registerLabel(register.role)}
              timeZone={timeZone}
              archived={!rights.canWrite || (meter.status !== "draft" && meter.status !== "active")}
              last={index === registers.length - 1}
            />
          ))
        )}
      </Card>

      {/**
       * **„Alle Datenpunkte des Geräts" steht im Entwurf unter der Karte** — und
       * beantwortet die Frage, die beim Binden als Nächstes kommt: Was bietet
       * das Gerät sonst noch an? Die Auswahl im Formular zeigt nur, was ein
       * Zählwerk speisen *kann*; ein Lesekopf meldet über fünfzig.
       *
       * **Nur bei genau einem Gerät**, dieselbe Regel wie in der Quellzeile und
       * in der Kopfleiste: Bei zweien führte ein Knopf ohne Namen zu einem von
       * beiden, und welchem, wüsste niemand.
       */}
      {deviceRoute !== null && (
        <div>
          <Button
            variant="secondary"
            size="sm"
            style={{ whiteSpace: "nowrap" }}
            onClick={() => navigate(deviceRoute)}
          >
            Alle Werte des Geräts
          </Button>
        </div>
      )}

      {rights.canWrite && (
        <p style={{ margin: 0, fontSize: 12, lineHeight: 1.55, color: "var(--text-muted)" }}>
          Gebunden wird in <strong>zwei Schritten</strong> — erst das Gerät, dann der Wert. Ein
          Lesekopf meldet über fünfzig; eine Liste über alle Geräte einer Anlage hätte mehrere hundert
          Einträge.
        </p>
      )}
    </Section>
  );
}

function RegisterRow({
  siteId,
  meter,
  register,
  label,
  timeZone,
  archived,
  last,
}: {
  siteId: string;
  meter: Meter;
  register: MeterRegister;
  /** Das Wort aus dem Messkonzept, sonst die Rolle (HM-090). */
  label: string;
  timeZone: string;
  /** An einem ausgebauten Zähler gibt es nichts mehr zu binden — und mit einer Lesefreigabe nichts zu ändern (HM-160). */
  archived: boolean;
  last: boolean;
}) {
  const [binding, setBinding] = useState(false);
  const source = register.source;

  /* Die zuletzt eingegangenen Werte des Geräts — der Schlüssel ist das Gerät,
     also teilen sich mehrere Zählwerke desselben Geräts eine Abfrage. */
  const values = useDatapointValues(source?.deviceId ?? null);
  const datapoints = useDatapoints(source?.deviceId ?? null);

  const latest =
    source === null
      ? undefined
      : values.data?.items.find((entry) => entry.datapointId === source.datapointId);
  const point =
    source === null
      ? undefined
      : datapoints.data?.items.find((entry) => entry.id === source.datapointId);

  const pointRoute: Route | null =
    source === null
      ? null
      : {
          kind: "datapoint",
          siteId,
          deviceId: source.deviceId,
          datapointId: source.datapointId,
          range: "24h",
        };

  return (
    <div style={{ borderBottom: last ? "none" : "1px solid var(--border-subtle)" }}>
      {/* Die Kopfzeile des Zählwerks — abgesetzt, wie im Entwurf. */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "var(--sp-3)",
          alignItems: "baseline",
          justifyContent: "space-between",
          padding: "11px var(--sp-5)",
          background: "var(--surface-inset)",
        }}
      >
        <span style={{ minWidth: 0 }}>
          <span
            style={{
              display: "block",
              fontSize: 13.5,
              fontWeight: 700,
              color: "var(--text-strong)",
              ...CLIP,
            }}
          >
            {label}
          </span>
          <span style={{ ...MONO, display: "block", fontSize: 11.5, color: "var(--text-faint)" }}>
            {registerRoleLabel(register.role)} · {registerKeyLabel(register)}
          </span>
        </span>
        {/* Ein abgeschaltetes Zählwerk sagt das — sonst suchte man den Grund
            dafür, dass nichts ankommt, bei der Bindung. */}
        {!register.enabled && <Badge tone="neutral">abgeschaltet</Badge>}
      </div>

      {/* **Ein virtueller Zähler hat Quellen, keine Quelle** (HM-123): je Gerät
          einer Anlage und je zusammengeschaltetem Zähler eine, alle gleichzeitig
          (#42). Die Bindung eines gewöhnlichen Zählwerks gibt es hier nicht. */}
      {meter.virtual && (
        <VirtualSourcesBlock siteId={siteId} meter={meter} register={register} timeZone={timeZone} archived={archived} />
      )}

      {/* Die Quellzeile. */}
      {!meter.virtual && (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "var(--sp-3)",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "13px var(--sp-5)",
        }}
      >
        <div style={{ flex: "1 1 260px", minWidth: 0 }}>
          {source === null ? (
            <>
              <span style={{ display: "block", fontSize: 13.5, color: "var(--text-faint)" }}>
                Keine Quelle
              </span>
              <span style={{ display: "block", fontSize: 11.5, color: "var(--text-faint)" }}>
                kein Wert gebunden
              </span>
            </>
          ) : (
            <>
              <a
                href={routePath(pointRoute!)}
                onClick={(event) => {
                  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
                    return;
                  }
                  event.preventDefault();
                  navigate(pointRoute!);
                }}
                style={{
                  display: "block",
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: "var(--text-strong)",
                  textDecoration: "none",
                  ...CLIP,
                }}
              >
                {source.deviceName} · {source.datapointName}
              </a>
              <span
                style={{ ...MONO, display: "block", fontSize: 11.5, color: "var(--text-faint)" }}
              >
                {source.datapointKey} · gebunden seit{" "}
                {formatInstant(new Date(source.since), timeZone, "de-DE", "date")}
              </span>
            </>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "var(--sp-3)", flexWrap: "wrap" }}>
          {/* Der zuletzt eingegangene Wert. **Fehlt er, steht dort nichts** —
              „noch kein Wert" nur, wenn ein Datenpunkt bekannt ist: An einem
              frisch gebundenen Zählwerk ist das der Normalfall (HM-049). */}
          {source !== null && latest !== undefined && point !== undefined && (
            <span style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
              <span
                style={{ ...MONO, fontSize: 13.5, fontWeight: 700, color: "var(--text-strong)" }}
              >
                {formatDatapointValue(latest.value, point)}
              </span>
            </span>
          )}
          {source !== null && latest === undefined && !values.isPending && (
            <span style={{ fontSize: 12.5, color: "var(--text-faint)" }}>noch kein Wert</span>
          )}

          {/* An einem ausgebauten Zähler gibt es nichts mehr zu binden: Ein
              Knopf, der mit `409` antwortet, ist kein Knopf (Regel 10). */}
          {/* **`secondary` und nicht `ghost`** (Entwurf): Der Entwurf setzt jede
              Handlung dieser Seite so, und hier ist es die Hauptaktion der
              Zeile — ein Geisterknopf daneben las sich wie ein Zusatz. */}
          {!archived && (
            <Button variant="secondary" size="sm" onClick={() => setBinding(!binding)}>
              {source === null ? "Quelle wählen …" : "Quelle ändern …"}
            </Button>
          )}
        </div>
      </div>
      )}

      {!meter.virtual && binding && (
        <div style={{ padding: "0 var(--sp-5) var(--sp-4)" }}>
          <BindingForm siteId={siteId} register={register} onDone={() => setBinding(false)} />
        </div>
      )}
    </div>
  );
}

/**
 * Die Quellen eines virtuellen Erzeugungszählers (HM-123, seit #42 aus Anlagen):
 * eine Zeile je Quelle mit ihrer Herkunft — Anlage oder Zähler — und ob sie
 * hinzu- oder abzählt. **Gewählt wird hier kein Datenpunkt**: Die Quellen folgen
 * aus den Anlagen hinter dem Zähler (geändert unter „Erzeugungsanlagen") und aus
 * den zusammengeschalteten Erzeugungszählern, die sich darunter ändern lassen.
 *
 * Am Zählwerk der Aufnahme stehen keine Quellen: Es wächst, wenn die Summe in
 * einer Viertelstunde fällt (`splitNetStands`).
 */
function VirtualSourcesBlock({
  siteId,
  meter,
  register,
  timeZone,
  archived,
}: {
  siteId: string;
  meter: Meter;
  register: MeterRegister;
  timeZone: string;
  archived: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const assets = useGenerationAssets(siteId).data?.items ?? [];
  const meters = useMeters(siteId, true).data?.items ?? [];
  const sourceRoute = (entry: MeterRegister["sources"][number]): Route => ({
    kind: "datapoint",
    siteId,
    deviceId: entry.deviceId,
    datapointId: entry.datapointId,
    range: "24h",
  });
  const originOf = (entry: MeterRegister["sources"][number]): string => {
    if (entry.generationAssetId !== null)
      return assets.find((asset) => asset.id === entry.generationAssetId)?.label ?? "Erzeugungsanlage";
    if (entry.sourceMeterId !== null)
      return meters.find((other) => other.id === entry.sourceMeterId)?.label ?? "Erzeugungszähler";
    return "von Hand gewählt";
  };

  if (register.role === "energy_export_total") {
    return (
      <div style={{ padding: "11px var(--sp-5)", fontSize: 13, color: "var(--text-muted)" }}>
        Die Aufnahme: wächst, wenn in einer Viertelstunde mehr in den Speicher geht, als die Anlagen
        abgeben. Abgabe minus Aufnahme ist die Summe der Quellen.
      </div>
    );
  }

  const memberIds = [
    ...new Set(register.sources.flatMap((entry) => (entry.sourceMeterId === null ? [] : [entry.sourceMeterId]))),
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      {register.sources.length === 0 && (
        <div style={{ padding: "13px var(--sp-5)", fontSize: 13, color: "var(--text-faint)" }}>
          Noch keine Quelle — hinter diesem Zähler steht keine Anlage, und er schaltet keinen Zähler
          zusammen.
        </div>
      )}
      {register.sources.map((entry) => (
        <div
          key={entry.datapointId}
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "var(--sp-3)",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "11px var(--sp-5)",
            borderBottom: "1px solid var(--border-subtle)",
          }}
        >
          <span style={{ minWidth: 0, flex: "1 1 260px" }}>
            <a
              href={routePath(sourceRoute(entry))}
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
                event.preventDefault();
                navigate(sourceRoute(entry));
              }}
              style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: "var(--text-strong)", textDecoration: "none", ...CLIP }}
            >
              {originOf(entry)} · {entry.datapointName}
            </a>
            <span style={{ ...MONO, display: "block", fontSize: 11.5, color: "var(--text-faint)" }}>
              {entry.deviceName} · {entry.sign === -1 ? "zählt ab" : "zählt hinzu"} · seit{" "}
              {formatInstant(new Date(entry.since), timeZone, "de-DE", "date")}
            </span>
          </span>
          <Badge tone={entry.sign === -1 ? "warning" : "neutral"}>{entry.sign === -1 ? "−" : "+"}</Badge>
        </div>
      ))}
      {!archived && (
        <div style={{ padding: "11px var(--sp-5)", display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
          <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, color: "var(--text-muted)" }}>
            Die Quellen folgen aus den Anlagen hinter diesem Zähler — zuordnen unter
            „Erzeugungsanlagen" — und aus den Erzeugungszählern, die er zusammenschaltet.
          </p>
          <div>
            <Button variant="secondary" size="sm" onClick={() => setEditing(!editing)}>
              Zusammengeschaltete Zähler …
            </Button>
          </div>
          {editing && (
            <SourceMetersForm
              meter={meter}
              candidates={meters.filter(
                (other) =>
                  other.kind === "generation" &&
                  !other.virtual &&
                  other.id !== meter.id &&
                  (other.status === "active" || other.status === "draft"),
              )}
              initial={memberIds}
              onDone={() => setEditing(false)}
            />
          )}
        </div>
      )}
    </div>
  );
}

/** Die Erzeugungszähler, die der virtuelle Zähler zusammenschaltet — die ganze Liste (#42). */
function SourceMetersForm({
  meter,
  candidates,
  initial,
  onDone,
}: {
  meter: Meter;
  candidates: readonly Meter[];
  initial: readonly string[];
  onDone: () => void;
}) {
  const client = useQueryClient();
  const [chosen, setChosen] = useState<readonly string[]>(initial);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: async () => {
      const { error: failure } = await api.PUT("/meters/{meterId}/source-meters", {
        params: { path: { meterId: meter.id } },
        body: { sourceMeterIds: [...chosen] },
      });
      if (failure) throw new Error(apiErrorMessage(failure) ?? "Die Zähler ließen sich nicht speichern.");
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["meters"] });
      onDone();
    },
    onError: (failure: Error) => setError(failure.message),
  });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)", padding: "var(--sp-4)", borderRadius: "var(--radius-md)", background: "var(--surface-sunken)" }}>
      {candidates.length === 0 && (
        <span style={{ fontSize: 13, color: "var(--text-muted)" }}>An dieser Anlage gibt es keinen weiteren Erzeugungszähler.</span>
      )}
      {candidates.map((other) => (
        <Checkbox
          key={other.id}
          checked={chosen.includes(other.id)}
          onChange={(on) => setChosen((current) => (on ? [...current, other.id] : current.filter((id) => id !== other.id)))}
          label={`${other.label} · ${meterNumberText(other)}`}
        />
      ))}
      {error !== null && <Alert tone="danger">{error}</Alert>}
      <div style={{ display: "flex", gap: "var(--sp-2)" }}>
        <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? "Wird gespeichert …" : "Speichern"}
        </Button>
        <Button variant="ghost" size="sm" onClick={onDone}>
          Abbrechen
        </Button>
      </div>
    </div>
  );
}

/**
 * Die Quelle setzen: erst das Gerät, dann der Datenpunkt.
 *
 * **Zwei Schritte, und der erste ist keine Umständlichkeit.** Ein Lesekopf
 * meldet über fünfzig Datenpunkte; eine einzige Liste über alle Geräte einer
 * Anlage hätte mehrere hundert Einträge und wäre die schlechteste Art, eine
 * Abrechnungsgrundlage zu wählen. Die Datenpunkte werden erst geholt, wenn ein
 * Gerät gewählt ist.
 *
 * **Angeboten wird nur, was ein Zählwerk speisen kann** (`canBindToRegister`):
 * eine Zahl, mit Rolle und Einheit, lesbar. Ein Schaltausgang steht nicht in
 * der Liste — der Server wiese ihn ab, und eine Auswahl, die zu einer Absage
 * führt, ist keine Auswahl.
 */
function BindingForm({
  siteId,
  register,
  onDone,
}: {
  siteId: string;
  register: MeterRegister;
  onDone: () => void;
}) {
  const client = useQueryClient();
  const devices = useDevices(siteId);
  const [deviceId, setDeviceId] = useState(register.source?.deviceId ?? "");
  const datapoints = useDatapoints(deviceId.length > 0 ? deviceId : null);
  const [datapointId, setDatapointId] = useState(register.source?.datapointId ?? "");
  const [error, setError] = useState<string | null>(null);

  const bindable = (datapoints.data?.items ?? []).filter(
    (point) =>
      point.valueType === "number" &&
      point.role !== null &&
      point.unit !== null &&
      point.access !== "write",
  );

  const invalidate = async (): Promise<void> => {
    await client.invalidateQueries({ queryKey: ["meters"] });
  };

  const bind = useMutation({
    mutationFn: async () => {
      const { error: failure } = await api.PUT("/meter-registers/{registerId}/binding", {
        params: { path: { registerId: register.id } },
        body: { datapointId },
      });
      if (failure) throw new Error(apiErrorMessage(failure) ?? "Die Bindung ließ sich nicht setzen.");
    },
    onSuccess: async () => {
      await invalidate();
      onDone();
    },
    onError: (failure: Error) => setError(failure.message),
  });

  const unbind = useMutation({
    mutationFn: async () => {
      const { error: failure } = await api.DELETE("/meter-registers/{registerId}/binding", {
        params: { path: { registerId: register.id } },
      });
      if (failure) throw new Error(apiErrorMessage(failure) ?? "Die Bindung ließ sich nicht lösen.");
    },
    onSuccess: async () => {
      await invalidate();
      onDone();
    },
    onError: (failure: Error) => setError(failure.message),
  });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--sp-3)",
        padding: "var(--sp-4)",
        borderRadius: "var(--radius-md)",
        background: "var(--surface-sunken)",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
          gap: "var(--sp-3)",
        }}
      >
        <Select
          label="Gerät"
          value={deviceId}
          onChange={(event) => {
            setDeviceId(event.target.value);
            // Der Datenpunkt des vorigen Geräts gehört nicht zum neuen.
            setDatapointId("");
          }}
          options={[
            { value: "", label: "Gerät wählen …" },
            ...(devices.data?.items ?? []).map((device) => ({
              value: device.id,
              label: device.name,
            })),
          ]}
        />
        <Select
          label="Wert"
          value={datapointId}
          disabled={deviceId.length === 0 || datapoints.isPending}
          onChange={(event) => setDatapointId(event.target.value)}
          options={[
            { value: "", label: "Wert wählen …" },
            ...bindable.map((point) => ({ value: point.id, label: point.name })),
          ]}
        />
      </div>

      {/**
       * **Ein Gerät ohne bindungsfähigen Datenpunkt sagt das.** Sonst stünde
       * eine leere Auswahl da, und man suchte den Fehler bei sich.
       */}
      {deviceId.length > 0 && !datapoints.isPending && bindable.length === 0 && (
        <p style={{ margin: 0, fontSize: 12.5, color: "var(--text-muted)" }}>
          Dieses Gerät meldet keinen Wert, der ein Zählwerk speisen kann — dafür braucht es
          eine gedeutete Kennzahl mit Einheit.
        </p>
      )}

      {error !== null && <Alert tone="danger">{error}</Alert>}

      <div style={{ display: "flex", gap: "var(--sp-2)", flexWrap: "wrap" }}>
        <Button
          size="sm"
          disabled={datapointId.length === 0 || bind.isPending}
          onClick={() => {
            setError(null);
            bind.mutate();
          }}
        >
          {bind.isPending ? "Wird gebunden …" : "Quelle setzen"}
        </Button>
        {register.source !== null && (
          <Button
            variant="secondary"
            size="sm"
            disabled={unbind.isPending}
            onClick={() => {
              setError(null);
              unbind.mutate();
            }}
          >
            Quelle lösen
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={onDone}>
          Abbrechen
        </Button>
      </div>

      <p style={{ margin: 0, fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5 }}>
        Die bisherige Bindung wird geschlossen, nicht überschrieben: Wer später wissen will, woher
        der Wert vom letzten März kam, findet die Bindung, die damals galt.
      </p>
    </div>
  );
}

/* --------------------------------------------------------------------------
 * Einstellungen · Was steht sonst fest?
 * ----------------------------------------------------------------------- */

/**
 * Was sonst über den Zähler feststeht.
 *
 * **Nur, was wirklich geliefert wurde** (HM-050). Geräte-ID und Hersteller
 * kommen aus dem Telegramm und fehlen an jedem von Hand angelegten Zähler; sie
 * stehen dann gar nicht da statt als „nicht gemeldet".
 */
function FactsSection({
  meter,
  timeZone,
  onEdit,
}: {
  meter: Meter;
  timeZone: string;
  /** Fehlt, wenn die Teilung kein Ändern erlaubt (HM-160). */
  onEdit?: (() => void) | undefined;
}) {
  /* Die Wohneinheit hängt an der Messstelle (HM-079); sie steht hier, weil
     jemand sie am Zähler sucht. Nur, wenn eine zugeordnet ist — „keine" wäre an
     einem Einfamilienhaus eine Zeile über etwas, das es dort nicht gibt. */
  const points = useMeteringPoints(meter.siteId);
  const areas = useSiteAreas(meter.siteId);
  const point = (points.data?.items ?? []).find((entry) => entry.id === meter.meteringPointId);
  const area = (areas.data?.items ?? []).find((entry) => entry.id === point?.siteAreaId);
  /* Die Erzeugungsanlagen hinter dieser Messstelle (HM-122) — nur, wenn es
     welche gibt: „keine" stünde an jedem Netzzähler über etwas, das dort nicht hingehört. */
  const assets = (useGenerationAssets(meter.siteId).data?.items ?? []).filter(
    (asset) => asset.meteringPointId === meter.meteringPointId,
  );

  const rows: Row[] = [
    ["Zustand", meterBadge(meter).label],
    ["Bauart", meterKindLabel(meter.kind, meter.commodity)],
    ["Zählwerke", String(meter.registers.length)],
  ];
  if (area !== undefined) rows.push(["Wohneinheit", area.name]);
  for (const asset of assets) {
    rows.push([
      asset.kind === "pv" ? "PV-Anlage dahinter" : "Speicher dahinter",
      [asset.label, generationAssetRating(asset)].filter((part) => part.length > 0).join(" · "),
    ]);
  }

  if (meter.meterNumber !== null) rows.push(["Zählernummer", meter.meterNumber]);
  if (meter.builtIntoDevice) rows.push(["Messwerk", "im Gerät, ohne Typenschild"]);
  if (meter.virtual) {
    const count = meter.registers[0]?.sources.length ?? 0;
    rows.push(["Art", `virtueller Erzeugungszähler — Summe aus ${count === 1 ? "einer Quelle" : `${count} Quellen`}`]);
  }
  if (meter.serialFromDevice !== null) {
    rows.push(["Geräte-ID (aus dem Telegramm)", meter.serialFromDevice]);
  }
  if (meter.manufacturer !== null) rows.push(["Hersteller", meter.manufacturer]);
  /**
   * **Das Wandlerverhältnis nur, wenn es eines gibt.** `1` heißt „direkt
   * gemessen" und ist an neunundneunzig von hundert Zählern der Fall; als Zeile
   * „Wandlerverhältnis: 1" stünde an jedem Zähler eine Angabe, die nichts sagt.
   */
  if (meter.transformerRatio !== 1) {
    rows.push(["Wandlerverhältnis", `${meter.transformerRatio} : 1`]);
  }
  if (meter.installedAt !== null) {
    rows.push(["Eingebaut", formatInstant(new Date(meter.installedAt), timeZone, "de-DE", "date")]);
  }
  rows.push(["Angelegt", formatInstant(new Date(meter.createdAt), timeZone, "de-DE", "date")]);

  return (
    <Section
      title="Stammdaten"
      subtitle="Was über diesen Zähler bekannt ist."
      /* **Nur zwei der Zeilen lassen sich ändern**, und der Knopf sagt nicht,
         welche — das steht im Dialog. Zustand, Bauart und Zählwerke folgen aus
         dem Zähler selbst, Geräte-ID und Hersteller aus dem Telegramm; sie hier
         als Felder anzubieten, wäre die unehrlichste Form von Anzeige. */
      action={
        onEdit !== undefined ? (
          <Button variant="secondary" size="sm" onClick={onEdit}>
            Ändern
          </Button>
        ) : undefined
      }
    >
      <Card>
        <DefinitionList rows={rows} />
      </Card>
    </Section>
  );
}

/* --------------------------------------------------------------------------
 * Einstellungen · Der rote Kasten
 * ----------------------------------------------------------------------- */

/** Archivieren — ausgebaut, ohne Nachfolger. */
function DangerSection({ meter, listRoute }: { meter: Meter; listRoute: Route }) {
  const client = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const archive = useMutation({
    mutationFn: async () => {
      const { error: failure } = await api.POST("/meters/{meterId}/archive", {
        params: { path: { meterId: meter.id } },
      });
      if (failure) {
        throw new Error(apiErrorMessage(failure) ?? "Der Zähler ließ sich nicht archivieren.");
      }
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["meters"] });
      navigate(listRoute);
    },
    onError: (failure: Error) => setError(failure.message),
  });

  /**
   * **Ein bereits archivierter oder abgelöster Zähler bekommt den Kasten
   * nicht** — sondern den Satz, der seinen Zustand erklärt. So macht es auch
   * der Entwurf: Ein Knopf, der mit `409` antwortet, ist kein Knopf (Regel 10),
   * eine leere Stelle an seiner Statt aber auch keine Auskunft.
   */
  if (meter.status !== "draft" && meter.status !== "active") {
    return (
      <Section title="Zähler verwalten">
        <Card>
          <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, color: "var(--text-muted)" }}>
            Dieser Zähler ist {meterBadge(meter).label.toLowerCase()}. Seine Quellbindungen sind
            geschlossen, seine Historie bleibt — wer wissen will, woher der Wert vom letzten März
            kam, findet die Bindung, die damals galt.
          </p>
        </Card>
      </Section>
    );
  }

  return (
    <Section title="Zähler verwalten">
      <Card>
        {/**
         * **Der Weg für einen Austausch steht vor dem roten Kasten** — wie bei
         * der Gerätefreigabe. Wer einen neuen Zähler bekommen hat, soll hier
         * nicht archivieren: Die Reihe liefe dann nicht über den Wechsel hinweg.
         */}
        <p style={{ margin: "0 0 var(--sp-3)", fontSize: 12.5, color: "var(--text-muted)" }}>
          Ist dieser Zähler durch einen neuen <strong>ersetzt</strong> worden, ist das ein
          Zählerwechsel und nicht ein Archivieren — dabei bleibt die Reihe über den Wechsel hinweg
          bruchfrei. Der Ablauf dafür kommt mit einem der nächsten Schritte.
        </p>

        {error !== null && <Alert tone="danger">{error}</Alert>}

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "var(--sp-3)",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "var(--sp-3)",
            border: "1px solid var(--hm-danger)",
            borderRadius: "var(--radius-md)",
          }}
        >
          <span style={{ display: "flex", flexDirection: "column", gap: 2, flex: "1 1 260px" }}>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: "var(--hm-danger)" }}>
              Zähler archivieren
            </span>
            <span style={{ fontSize: 12.5, lineHeight: 1.5, color: "var(--text-muted)" }}>
              Der Zähler gilt als ausgebaut und verschwindet aus der Liste. Seine laufenden Quellen
              werden gelöst, damit kein Wert eines anderen Zählers bei ihm landet. Seine{" "}
              <strong>Zählerstände bleiben erhalten</strong> — an einem Zähler wird nichts
              gelöscht.
            </span>
          </span>

          <span style={{ display: "flex", gap: "var(--sp-2)", flexWrap: "wrap" }}>
            {confirming ? (
              <>
                <Button
                  variant="danger"
                  size="sm"
                  disabled={archive.isPending}
                  onClick={() => archive.mutate()}
                >
                  {meter.label} wirklich archivieren
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                  Abbrechen
                </Button>
              </>
            ) : (
              <Button variant="secondary" size="sm" onClick={() => setConfirming(true)}>
                Archivieren …
              </Button>
            )}
          </span>
        </div>
      </Card>
    </Section>
  );
}

/** Die eine Kennzahl einer Erzeugungsanlage: Nennleistung oder Kapazität (HM-122). */
function generationAssetRating(asset: {
  readonly kind: "pv" | "battery";
  readonly peakPowerKwp: number | null;
  readonly capacityKwh: number | null;
}): string {
  const format = (value: number) => new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 }).format(value);
  if (asset.kind === "pv") return asset.peakPowerKwp === null ? "" : `${format(asset.peakPowerKwp)} kWp`;
  return asset.capacityKwh === null ? "" : `${format(asset.capacityKwh)} kWh`;
}
